import { useEffect, useRef } from 'react';
import { useAgoraChat } from '../context/AgoraChatContext';
import { useMeetingState } from '../context/MeetingStateContext';

/**
 * useMeetingStateSync — Bridge hook
 *
 * Automatically syncs Agora Chat messages to the MeetingStateContext
 * backend so that late joiners can retrieve chat history.
 *
 * Mount this hook once inside any component that is a child of both
 * AgoraChatProvider and MeetingStateProvider (e.g., MeetingContainerNew).
 */
export function useMeetingStateSync() {
  const { messages: chatMessages } = useAgoraChat();
  const meetingState = useMeetingState();
  const lastSyncedCountRef = useRef(0);

  // ─── Sync new Agora Chat messages to backend ───
  useEffect(() => {
    if (!chatMessages || !meetingState?.emitChatMessage) return;

    const newMessages = chatMessages.slice(lastSyncedCountRef.current);
    if (newMessages.length === 0) return;

    lastSyncedCountRef.current = chatMessages.length;

    newMessages.forEach(msg => {
      // Only sync local messages to avoid duplicates
      if (msg.isLocal) {
        meetingState.emitChatMessage({
          id: msg.id,
          text: msg.text,
          userId: msg.userId,
          userName: msg.userName,
          timestamp: msg.timestamp,
          image: msg.image || null,
          file: msg.file || null,
          replyTo: msg.replyTo || null,
        });
      }
    });
  }, [chatMessages?.length, meetingState?.emitChatMessage]);

  return {
    allMessages: getMergedMessages(meetingState.chatHistory, chatMessages || []),
    pinnedMessages: meetingState.pinnedMessages,
    meetingStartedAt: meetingState.meetingStartedAt,
    isSnapshotLoaded: meetingState.isSnapshotLoaded,
  };
}

/**
 * Merge snapshot chat history with real-time RTM messages.
 * Deduplicates by message ID and sorts by timestamp.
 */
function getMergedMessages(snapshotMessages, rtmMessages) {
  if (!snapshotMessages?.length) return rtmMessages;

  const messageMap = new Map();

  // Add snapshot messages first (older)
  snapshotMessages.forEach(msg => {
    messageMap.set(msg.id, { ...msg, isFromSnapshot: true });
  });

  // Add/overwrite with RTM messages (newer take precedence)
  rtmMessages.forEach(msg => {
    messageMap.set(msg.id, msg);
  });

  // Sort by timestamp
  return Array.from(messageMap.values()).sort((a, b) => a.timestamp - b.timestamp);
}

export default useMeetingStateSync;
